// Tool-config delta + authoring builder (fn-69 narrowing + fn-71 R10 authoring).
//
// PURE + exported so it unit-tests without mounting the panel. This is the
// SINGLE SOURCE for the tool-config selection/build logic. It serves BOTH
// panel modes:
//
//   - **narrowing** (fn-69 per-session): turns the panel's per-tool /
//     per-connector / per-built-in selection into a `SessionToolConfigOverride`
//     (the caller DELTA — `schemas`), enforcing the **narrowing-only** invariant
//     client-side so the wire body never even attempts to widen.
//   - **authoring** (fn-71 baseline-authoring): turns the SAME selection into an
//     `AuthoringToolConfig` — the agent's FULL desired baseline (every tool's
//     chosen permission, every connector, every built-in), where `always_allow`
//     is a first-class choice and there is NO narrowing clamp.
//
// narrowing wire mapping:
//   - UI label → wire enum: **Allowed → `baseline`** (a no-op, so it is simply
//     OMITTED), **Needs-approval → `always_ask`** (only emitted when it is a
//     real escalation from an `always_allow` baseline), **Disabled →
//     `disabled`**. There is NO `always_allow` in the wire — a caller can never
//     de-escalate.
//   - **Denylist-locked** tools force `always_ask`; "Allowed" is never
//     selectable, and a clamped "Allowed" resolves to the baseline no-op (stays
//     at ask), never a widen.
//   - **`always_deny`-locked** tools are excluded from the resolved scope by the
//     CP already; the UI renders them locked-disabled and the builder emits
//     nothing for them (no per-tool entry can re-admit them).
//   - **Native built-ins** map to `native_builtins: Record<name, boolean>` — we
//     only ever emit a DISABLE of a baseline-enabled built-in (narrowing); a
//     baseline-disabled built-in can never be turned on (would widen).
//
// The CP resolver is the authoritative narrowing/denylist enforcer; the
// narrowing path mirrors the same invariants purely as a UX + defense-in-depth
// layer so a UI bug can never construct a widening body. The authoring path is
// the agent-create/edit baseline, so it deliberately allows `always_allow` and
// enabling a baseline-disabled built-in — but the platform denylist
// (`denylist_locked`) and `always_deny` locks STILL pin (a billing/trust
// mutation is never offerable as Allowed at baseline either).
/**
 * Is the "Allowed" choice offered for this tool?
 *
 *   - **narrowing** (default): only when the agent's baseline already permits it
 *     (`always_allow`) AND it is neither denylist- nor `always_deny`-locked —
 *     the narrowing-only gate the UI uses to disable the "Allowed" segment.
 *   - **authoring**: for ANY tool that is not denylist- / `always_deny`- /
 *     `always_allow`-locked (the author may grant `always_allow` regardless of
 *     the current baseline) — there is no narrowing clamp.
 *
 * **fn-74 (R5, HITL lock)** — a tool flagged `always_allow_locked` (every DIRECT
 * / `supports_direct_introspection` connector tool, e.g. InfoTrack) NEVER offers
 * "Allowed" in EITHER mode: a direct tool is reached outside the AgentCore
 * gateway, so MA HITL (`always_ask`) is the only control and `always_allow` must
 * not be authorable. The 3-state control then shows only Always-ask / Disabled.
 */
export function isAllowedSelectable(tool, mode = 'narrowing') {
    if (tool.denylist_locked || isAlwaysDeny(tool) || tool.always_allow_locked === true)
        return false;
    if (mode === 'authoring')
        return true;
    return tool.baseline_permission === 'always_allow';
}
/**
 * Does this tool's baseline make it always-deny (locked-disabled / not callable)?
 * True for either the explicit `always_deny_locked` projection OR a
 * `baseline_permission === 'always_deny'` — both mean the tool is excluded from
 * scope and can never be re-admitted, and we treat them identically so a
 * (defensively) un-flagged `always_deny` baseline can never be widened.
 */
function isAlwaysDeny(tool) {
    return tool.always_deny_locked || tool.baseline_permission === 'always_deny';
}
/** Classify a tool's lock (drives the locked-disabled / locked-ask rendering). */
export function toolLock(tool) {
    if (isAlwaysDeny(tool))
        return 'always_deny';
    if (tool.denylist_locked)
        return 'denylist';
    // fn-74 — a DIRECT-connector tool pinned at `always_ask` (not denylisted): the
    // author can still Disable it, but "Allowed" is never offered.
    if (tool.always_allow_locked === true)
        return 'hitl_locked';
    return null;
}
/**
 * The default 3-state selection for a tool — what the control shows before the
 * user touches it. `always_deny` → disabled (locked); denylist → needs-approval
 * (its forced baseline); otherwise the agent's declared baseline permission.
 * Mode-independent: the seed always reflects the projected baseline (authoring
 * differs only in which choices are SELECTABLE, not in the default).
 */
export function defaultToolSelection(tool) {
    if (isAlwaysDeny(tool))
        return 'disabled';
    if (tool.denylist_locked)
        return 'needs_approval';
    switch (tool.baseline_permission) {
        case 'always_allow':
            return 'allowed';
        case 'always_ask':
            return 'needs_approval';
        case 'always_deny':
            return 'disabled';
        default:
            return 'needs_approval';
    }
}
/**
 * Build the fully-defaulted panel state from a config-list response — every
 * connector enabled, every tool at its baseline selection, every built-in at
 * its baseline enabled state. The UI seeds its `useState` with this and resets
 * to it when the selected agent changes.
 */
export function defaultPanelState(response) {
    // `Object.create(null)` — these maps are keyed by EXTERNAL ids (server_id /
    // tool_id / built-in name) from the config-list payload; a null prototype
    // forecloses prototype pollution via a `__proto__`/`constructor` id.
    const connectorsDisabled = Object.create(null);
    const toolSelections = Object.create(null);
    for (const connector of response.connectors) {
        connectorsDisabled[connector.server_id] = false;
        for (const tool of connector.tools) {
            toolSelections[tool.tool_id] = defaultToolSelection(tool);
        }
    }
    const builtinsEnabled = Object.create(null);
    for (const b of response.native_builtins) {
        builtinsEnabled[b.name] = b.baseline_enabled;
    }
    return { connectorsDisabled, toolSelections, builtinsEnabled };
}
/**
 * Map a tool's CURRENTLY-APPLIED override permission (the session config-list
 * `applied_permission`) to a 3-state selection, clamped narrowing-only. Used by
 * the in-session dropdown so the panel seeds from the override actually in
 * force, not the agent baseline. `baseline` (or absent) ⇒ the tool's baseline
 * selection; `always_ask` ⇒ needs-approval; `disabled` ⇒ disabled. A locked
 * tool always pins to its forced state regardless of a stale applied value.
 */
function appliedToolSelection(tool) {
    if (isAlwaysDeny(tool))
        return 'disabled';
    const applied = tool.applied_permission;
    if (applied === 'disabled')
        return 'disabled';
    if (applied === 'always_ask')
        return 'needs_approval';
    // `baseline` or no applied override → fall back to the baseline selection
    // (which itself pins a denylist-locked tool to needs-approval).
    return defaultToolSelection(tool);
}
/**
 * Build panel state from a SESSION config-list response, seeding from the
 * CURRENTLY-APPLIED override (`applied_permission` / `applied_disabled` /
 * `applied_enabled`) rather than the bare agent baseline — so the in-session
 * dropdown opens reflecting the override actually in force (incl. after a
 * reload). Falls back to baseline for any tool/connector/built-in the session
 * has not overridden. Narrowing-only is preserved (applied values are
 * themselves narrowing-only, and locks still pin).
 */
export function appliedPanelState(response) {
    // Null-prototype maps — keyed by external server_id / tool_id / built-in name
    // (prototype-pollution-safe; see `defaultPanelState`).
    const connectorsDisabled = Object.create(null);
    const toolSelections = Object.create(null);
    for (const connector of response.connectors) {
        connectorsDisabled[connector.server_id] = connector.applied_disabled ?? false;
        for (const tool of connector.tools) {
            toolSelections[tool.tool_id] = appliedToolSelection(tool);
        }
    }
    const builtinsEnabled = Object.create(null);
    for (const b of response.native_builtins) {
        builtinsEnabled[b.name] = b.applied_enabled ?? b.baseline_enabled;
    }
    return { connectorsDisabled, toolSelections, builtinsEnabled };
}
/**
 * Is `sel` a legal choice for `tool` given its (possibly new) baseline + mode?
 * Used to clamp a stale selection when the config-list payload changes for the
 * SAME agent (e.g. a degraded → recovered retry). `disabled` and
 * `needs_approval` are always legal for a normal tool; `allowed` only when the
 * mode + baseline permit it; a locked tool is pinned to its forced state.
 */
function isSelectionLegal(tool, sel, mode = 'narrowing') {
    if (isAlwaysDeny(tool))
        return sel === 'disabled';
    if (tool.denylist_locked)
        return sel === 'needs_approval' || sel === 'disabled';
    if (sel === 'allowed')
        return isAllowedSelectable(tool, mode);
    return true;
}
/**
 * Merge a fresh config-list `response` onto a `prior` panel state, preserving
 * in-progress edits while (a) seeding fresh defaults for tools/connectors/
 * built-ins that only just arrived (the degraded → recovered retry case) and
 * (b) clamping any stale selection that is no longer legal against the new
 * payload, and dropping entries no longer present.
 *
 * `base` is the from-scratch seed for ANY entry not carried over from `prior`
 * (a newly-arrived tool, or one whose stale selection was clamped). It MUST be
 * the same builder the panel seeded with — {@link defaultPanelState} pre-start,
 * {@link appliedPanelState} in-session — otherwise a newly-arrived tool that
 * the session has DISABLED would seed from baseline (allowed) and a subsequent
 * full-replacement apply would silently re-enable it (the in-session leak found
 * in impl-review). Every entry in `response` ends up with a concrete selection,
 * so the display and the built override never diverge.
 */
export function mergePanelState(response, prior, base = defaultPanelState, mode = 'narrowing') {
    const fresh = base(response);
    // Null-prototype accumulators — keyed by external ids (pollution-safe).
    const connectorsDisabled = Object.create(null);
    for (const connector of response.connectors) {
        const id = connector.server_id;
        // Keep a prior toggle when the user already had one; else the fresh
        // (applied-or-baseline) default for a newly-arrived connector.
        connectorsDisabled[id] = Object.hasOwn(prior.connectorsDisabled, id)
            ? (prior.connectorsDisabled[id] ?? false)
            : (fresh.connectorsDisabled[id] ?? false);
    }
    const toolSelections = Object.create(null);
    for (const connector of response.connectors) {
        for (const tool of connector.tools) {
            const priorSel = prior.toolSelections[tool.tool_id];
            toolSelections[tool.tool_id] =
                priorSel !== undefined && isSelectionLegal(tool, priorSel, mode)
                    ? priorSel
                    : (fresh.toolSelections[tool.tool_id] ?? defaultToolSelection(tool));
        }
    }
    const builtinsEnabled = Object.create(null);
    for (const b of response.native_builtins) {
        builtinsEnabled[b.name] = Object.hasOwn(prior.builtinsEnabled, b.name)
            ? (prior.builtinsEnabled[b.name] ?? b.baseline_enabled)
            : (fresh.builtinsEnabled[b.name] ?? b.baseline_enabled);
    }
    return { connectorsDisabled, toolSelections, builtinsEnabled };
}
// ── narrowing builder (fn-69) ─────────────────────────────────────────────
/**
 * Resolve one tool's selection to the wire enum, or `undefined` for a baseline
 * no-op (omitted from the delta). Narrowing-only is enforced here defensively —
 * an "Allowed" that the baseline does not permit is clamped to its baseline,
 * never widened.
 */
function resolveToolWire(tool, selection) {
    // `always_deny` (flagged OR a bare `always_deny` baseline) is excluded from
    // scope by the CP; nothing to emit and a per-tool entry could not re-admit it
    // anyway. Critically this prevents widening a (possibly un-flagged)
    // `always_deny` baseline to `always_ask` via the fall-through below.
    if (isAlwaysDeny(tool))
        return undefined;
    if (selection === 'disabled')
        return 'disabled';
    // "Allowed" is a real choice only when the baseline permits it; otherwise it
    // is clamped to the tool's baseline (no widening).
    if (selection === 'allowed' && isAllowedSelectable(tool))
        return undefined; // == baseline
    // Remaining: needs-approval, or a clamped "Allowed". Emit `always_ask` ONLY
    // when it is a genuine escalation from an `always_allow` baseline; a tool
    // already at ask (declared `always_ask` or denylist-forced) is a no-op.
    const baselineAlreadyAsk = tool.baseline_permission === 'always_ask' || tool.denylist_locked;
    if (baselineAlreadyAsk)
        return undefined;
    return 'always_ask';
}
/** Build one connector's server-override fragment, or `undefined` for a no-op. */
function resolveConnector(connector, state) {
    if (state.connectorsDisabled[connector.server_id] === true) {
        return { disabled: true };
    }
    // Null-prototype map — keyed by external tool_id (pollution-safe).
    const tools = Object.create(null);
    for (const tool of connector.tools) {
        const selection = state.toolSelections[tool.tool_id] ?? defaultToolSelection(tool);
        const wire = resolveToolWire(tool, selection);
        if (wire !== undefined)
            tools[tool.tool_id] = wire;
    }
    return Object.keys(tools).length > 0 ? { tools } : undefined;
}
/**
 * Build the `SessionToolConfigOverride` delta from the panel state. Omits every
 * baseline no-op (so an untouched panel yields `{}` — pure baseline). The
 * result is narrowing-only by construction.
 */
export function buildToolConfigOverride(response, state) {
    const override = {};
    // Null-prototype accumulators — keyed by external server_id / built-in name
    // (pollution-safe; see `defaultPanelState`).
    const servers = Object.create(null);
    for (const connector of response.connectors) {
        const fragment = resolveConnector(connector, state);
        if (fragment !== undefined)
            servers[connector.server_id] = fragment;
    }
    if (Object.keys(servers).length > 0)
        override.servers = servers;
    const nativeBuiltins = Object.create(null);
    for (const b of response.native_builtins) {
        const enabled = state.builtinsEnabled[b.name] ?? b.baseline_enabled;
        // Narrowing-only: only a DISABLE of a baseline-enabled built-in is a valid
        // override. Enabling a baseline-disabled built-in would widen → never emit.
        if (b.baseline_enabled && !enabled)
            nativeBuiltins[b.name] = false;
    }
    if (Object.keys(nativeBuiltins).length > 0)
        override.native_builtins = nativeBuiltins;
    return override;
}
// ── authoring builder (fn-71 R10) ─────────────────────────────────────────
/**
 * Resolve one tool's selection to the AUTHORING permission enum (full, not a
 * delta). `always_deny` pins to disabled; a `Disabled` selection ALWAYS wins
 * (the panel lets a denylist-locked tool be set to Disabled — only "Allowed" is
 * blocked for it — so the saved config must match the shown state); the denylist
 * then pins the remaining choices to `always_ask` (never `always_allow`). A
 * clamped "Allowed" that the mode does not permit falls back to `always_ask`
 * rather than widening. A DIRECT-connector tool (`always_allow_locked`, fn-74)
 * is NEVER `always_allow`: `isAllowedSelectable` returns `false` for it, so a
 * stale "Allowed" selection falls through to `always_ask` — defense-in-depth so
 * the UI can never author a non-HITL direct tool.
 */
function resolveAuthoringToolPermission(tool, selection) {
    if (isAlwaysDeny(tool))
        return 'disabled';
    // `Disabled` wins before the denylist pin so the displayed state and the saved
    // config never diverge (mirrors narrowing's `resolveToolWire`, where a
    // disabled denylist tool resolves to `disabled`).
    if (selection === 'disabled')
        return 'disabled';
    if (tool.denylist_locked)
        return 'always_ask';
    if (selection === 'allowed') {
        // `isAllowedSelectable` is `false` for a denylist- / `always_deny`- /
        // `always_allow_locked` tool, so a clamped "Allowed" resolves to `always_ask`.
        return isAllowedSelectable(tool, 'authoring') ? 'always_allow' : 'always_ask';
    }
    return 'always_ask';
}
/**
 * Build the agent's FULL desired baseline tool config from the panel state
 * (fn-71 authoring). Unlike {@link buildToolConfigOverride} this is NOT a delta:
 * every connector + tool + built-in is represented, and `always_allow` is a
 * first-class choice. The console Add-Agent form converts this to MA-native
 * `tools[]` (mcp_toolset `configs[]` + native-built-in toggles).
 *
 * A disabled connector still emits `disabled: true` but its tools are still
 * enumerated (so a later re-enable in the form has the per-tool baselines).
 *
 * A `catalog_unavailable` connector emits NO per-tool baselines (`tools: {}`)
 * and is flagged `catalogUnavailable: true` — its tools are UNKNOWN, not empty,
 * so the consumer must preserve / whole-server-grant it rather than save an
 * empty (deny-all) allow-list that would drop every tool on the connector.
 */
export function buildAuthoringToolConfig(response, state) {
    // Null-prototype accumulators — keyed by external ids (pollution-safe).
    const servers = Object.create(null);
    for (const connector of response.connectors) {
        const tools = Object.create(null);
        // A catalog-unavailable connector can't be enumerated — leave `tools`
        // empty + flag it so the consumer never reads `{}` as a deny-all baseline.
        if (!connector.catalog_unavailable) {
            for (const tool of connector.tools) {
                const selection = state.toolSelections[tool.tool_id] ?? defaultToolSelection(tool);
                tools[tool.tool_id] = resolveAuthoringToolPermission(tool, selection);
            }
        }
        servers[connector.server_id] = {
            disabled: state.connectorsDisabled[connector.server_id] === true,
            catalogUnavailable: connector.catalog_unavailable,
            tools,
        };
    }
    const nativeBuiltins = Object.create(null);
    for (const b of response.native_builtins) {
        // Authoring: any boolean is legal — the author may enable a baseline-
        // disabled built-in (no narrowing clamp).
        nativeBuiltins[b.name] = state.builtinsEnabled[b.name] ?? b.baseline_enabled;
    }
    return { servers, nativeBuiltins, catalogAvailable: response.catalog_available };
}
//# sourceMappingURL=delta.js.map